import { JobApplicationCard, JobApplicationCardProps } from '@restworld/ui-components';
import Image from 'next/image';
import { cn, customImageLoader, getAssetSrc } from '@restworld/utils-common';
import React, { useCallback } from 'react';
import { Button, Heading, Text } from '@restworld/ui-ds-base';
import NextLink from 'next/link';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useTrackJobApplication } from '../hooks/use-track-job-application';
import { useTranslation } from 'next-i18next';

import { MarketingConsentCard } from '../../marketing-consent';

export type JobApplicationTabContentProps = {
  title: string;
  applications: JobApplicationCardProps[];
  status: JobApplicationStatus;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
};

export enum JobApplicationStatus {
  IN_EVALUATION = 'in_evaluation',
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
}

export const JobApplicationTabContent = ({
  title,
  applications,
  status = JobApplicationStatus.POSITIVE,
  onLoadMore,
  hasMore = false,
  loading = false,
}: JobApplicationTabContentProps) => {
  const { trackJobApplicationOpened } = useTrackJobApplication();
  const { t } = useTranslation('job-application-page');

  if (applications.length > 0) {
    return (
      <div className="flex flex-col w-full items-start justify-start h-full lg:gap-6 gap-8">
        <Text size={2}>{title}</Text>
        {/* Workers created without a screen (Hirematic/Indeed): the optional signup box, on every tab. */}
        <MarketingConsentCard placement="applications" className="w-full" />
        <div className="flex flex-col items-start w-full gap-4">
          {applications.map((application, index) => (
            <JobApplicationCard
              key={index}
              {...application}
              onRedirect={() => {
                trackJobApplicationOpened(application.statusKey);
              }}
            />
          ))}
        </div>
        {onLoadMore && (
          <div className="flex justify-center w-full">
            <Button variant="tertiary" onClick={onLoadMore} loading={loading} disabled={loading || !hasMore}>
              {t('load_more')}
            </Button>
          </div>
        )}
      </div>
    );
  }
  return <NotFoundComponent status={status} />;
};

export type NotFoundComponentProps = {
  status;
};

export const NotFoundComponent = ({ status }: NotFoundComponentProps) => {
  const { t } = useTranslation();
  const getStatusTitle = useCallback(() => {
    switch (status) {
      case JobApplicationStatus.POSITIVE:
        return t('job-application-page:no_applications');
      case JobApplicationStatus.NEGATIVE:
        return t('job-application-page:your_historian');
      case JobApplicationStatus.IN_EVALUATION:
        return t('job-application-page:all_from_us');
      default:
        return undefined;
    }
  }, [t, status]);

  const getStatusSubTitle = useCallback(() => {
    switch (status) {
      case JobApplicationStatus.POSITIVE:
        return t('job-application-page:your_profile_is_a_blank_canvas_to_fill');
      case JobApplicationStatus.NEGATIVE:
        return t('job-application-page:when_you_make_it_and_when_you_dont_we_are_here_for_you');
      case JobApplicationStatus.IN_EVALUATION:
        return t('job-application-page:here_you_will_find_the_applications_waiting_for_us_to_proceed');

      default:
        return undefined;
    }
  }, [t, status]);

  const titleText = getStatusTitle();

  return (
    <div className="flex flex-col items-center justify-start h-full w-full gap-0">
      <StyledImage
        status={status}
        src={
          status === JobApplicationStatus.POSITIVE
            ? getAssetSrc('/utils-assets/man-with-a-dog.svg')
            : getAssetSrc('/utils-assets/not-found.svg')
        }
        width={352}
        height={259}
        alt={t('job-application-page:job_application_not_found')}
        loader={customImageLoader}
      />
      <div className="flex flex-col items-center lg:w-[411px] w-full lg:mt-6 mt-2.5 mb-6 text-center gap-2">
        <Heading size={3} weight="bold" className="hidden lg:block">
          {titleText}
        </Heading>
        <Heading size={4} className="lg:hidden">
          {titleText}
        </Heading>
        <Heading size={5} weight="normal" color="muted-foreground">
          {getStatusSubTitle()}
        </Heading>
      </div>
    </div>
  );
};

export const ViewOtherPositionsButton = () => {
  const { t } = useTranslation();
  return (
    <NextLink href="/cerco-lavoro" className="text-primary-800 cursor-pointer w-fit">
      <Button className={'lg:min-w-[238px] min-w-[229px] gap-2 whitespace-nowrap'}>
        <div className="inline-flex gap-2 items-center">
          <span>{t('job-application-page:discover_other_positions')}</span>{' '}
          <ArrowTopRightOnSquareIcon className="h-5 w-5 text-black" />
        </div>
      </Button>
    </NextLink>
  );
};

const StyledImage = ({
  status,
  className,
  ...props
}: Pick<NotFoundComponentProps, 'status'> & Omit<React.ComponentProps<typeof Image>, 'status'>) => (
  <Image
    className={cn(
      status === JobApplicationStatus.POSITIVE && 'lg:w-[352px] lg:h-[269px] w-[287px] h-[220px]',
      (status === JobApplicationStatus.NEGATIVE || status === JobApplicationStatus.IN_EVALUATION) &&
        'lg:w-[273px] lg:h-[252px] w-[238px] h-[220px]',
      className
    )}
    {...props}
  />
);
